import * as SignalR from "@microsoft/signalr";
import appConfig from "@/appConfig";
import * as Hub from "@dux/hub";
import { RequestErrorConnection } from "@errors/RequestError";
import { sessionError } from "@dux/session";

// The server may deliver one event several times (role group + person push,
// bus redelivery, one message per changed artifact). It stamps a messageId on
// every event so duplicates can be dropped here; keep a bounded window of ids.
const SEEN_MESSAGE_LIMIT = 500;

const createHubMiddleware = () => {
  let connection;
  const seenMessageIds = [];
  const seenMessageSet = new Set();

  const isDuplicate = (messageId) => {
    if (!messageId) return false;
    if (seenMessageSet.has(messageId)) return true;
    seenMessageSet.add(messageId);
    seenMessageIds.push(messageId);
    if (seenMessageIds.length > SEEN_MESSAGE_LIMIT)
      seenMessageSet.delete(seenMessageIds.shift());
    return false;
  };

  const disconnect = () => {
    if (!connection) return;
    try {
      connection.stop();
    } catch (x) {
    } finally {
      connection = null;
    }
  };

  const connect = ({ dispatch }) => {
    if (connection) return;

    let origin = window.location.origin;
    let hubRoot = !appConfig.hubRoot.startsWith("http")
      ? origin + "/hub"
      : appConfig.hubRoot;
    connection = new SignalR.HubConnectionBuilder()
      .withUrl(hubRoot + "/session")
      .build();
    connection.on("Event", ({ id, data, messageId }) => {
      if (isDuplicate(messageId)) return;
      dispatch(Hub.hubEvent(id, data));
    });
    connection.onclose((error) => {
      if (!error) return;
      dispatch(Hub.hubReconnect());
      dispatch(Hub.hubDisconnect());
    });
    connection.start();
  };

  const reconnect = ({ dispatch }) => {
    disconnect();
    connect({ dispatch });
  };

  const invoke = (command, data, callback) => {
    if (connection.state === "Connected") {
      if (callback) {
        connection.invoke("InvokeCommand", command, data).then(callback);
      } else {
        connection.invoke("InvokeCommand", command, data);
      }
    } else {
      callback && callback({ success: false, errorMessage: "HubInvalidState" });
    }
  };

  return (store) => (next) => (action) => {
    switch (action.type) {
      case Hub.HUB_CONNECT:
        connect(store);
        next(action);
        break;

      case Hub.HUB_DISCONNECT:
        store.dispatch(sessionError(RequestErrorConnection()));
        disconnect(store);
        next(action);
        break;

      case Hub.HUB_RECONNECT:
        reconnect(store);
        next(action);
        break;

      case Hub.HUB_COMMAND:
        invoke(
          action.payload.command,
          action.payload.data,
          action.payload.callback
        );
        next(action);
        break;

      default:
        next(action);
        break;
    }
  };
};

export default createHubMiddleware;
